import { useCallback, useEffect, useRef, useState } from "react"
import { IconCalendar, IconHistory, IconLoader2, IconReportMoney } from "@tabler/icons-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/api"
import { useBenefitEditRegistration } from "@/components/benefit/benefit-edit-scope"
import type { BenefitPledgeGeneration } from "@/lib/benefit-types"
import { pledgeReportFlagSourceLabel, pledgeReportFlags } from "@/lib/pledge-report"
import { pacificTime, pacificToday } from "@/lib/pledge-submissions"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"

interface BenefitPledgeReport {
  businessDate: string
  pledges: BenefitPledgeGeneration[]
  removedFollowUp: BenefitPledgeGeneration[]
}

function PledgeRows({ rows, removed }: { rows: BenefitPledgeGeneration[]; removed?: boolean }) {
  if (rows.length === 0) {
    return <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">No rows for this business date.</TableCell></TableRow>
  }
  const flagSource = pledgeReportFlagSourceLabel(removed === true)
  return <>{rows.map((row) => <TableRow key={`${row.generationId}-${removed ? "removed" : "pledged"}`}>
    <TableCell className="font-mono text-xs">{row.benefitId}</TableCell>
    <TableCell>{row.programId}/{row.programYear}</TableCell>
    <TableCell>{row.vendorId}</TableCell>
    <TableCell className="text-right">${row.amount.toFixed(2)}</TableCell>
    <TableCell>{row.accountNumber}</TableCell>
    <TableCell><Badge variant={removed ? "destructive" : "secondary"}>{removed ? "Removed" : row.status}</Badge></TableCell>
    <TableCell>
      <div aria-label={flagSource} className="flex flex-wrap gap-1">
        {pledgeReportFlags(row).map((flag) => <Badge key={flag.label} variant={flag.enabled ? "default" : "outline"}>
          {flag.label}: {flag.enabled ? "Yes" : "No"}
        </Badge>)}
      </div>
    </TableCell>
    <TableCell className="text-xs">{pacificTime(removed ? (row.removalAt ?? row.createdAt) : row.createdAt)}</TableCell>
  </TableRow>)}</>
}

export default function PledgeReportsPage() {
  const permissions = useAppSelector((state) => state.permissions.permissions)
  const canView = hasPermission(permissions, "benefit-runs:view")
  const [date, setDate] = useState(pacificToday)
  const [report, setReport] = useState<BenefitPledgeReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [capability, setCapability] = useState<{ canPublish: boolean; disabledReason: string | null } | null>(null)
  const [publishing, setPublishing] = useState(false)
  const publishInFlight = useRef(false)
  const [operationId, setOperationId] = useState<string | null>(null)
  const [published, setPublished] = useState<string | null>(null)
  const register = useBenefitEditRegistration()

  useEffect(() => {
    if (!canView) return
    let active = true
    api.get<{ canPublish: boolean; disabledReason: string | null }>("/api/pledges/capability")
      .then(value => { if (active) setCapability(value) })
      .catch(() => { if (active) setCapability({ canPublish: false, disabledReason: "Publication capability could not be verified." }) })
    return () => { active = false }
  }, [canView])

  const load = useCallback(async () => {
    if (!canView) return
    setLoading(true)
    setError(null)
    try {
      setReport(await api.get<BenefitPledgeReport>(`/api/pledges/daily/${date}`))
    } catch (err) {
      setError(err instanceof Error ? err.message : "The pledge report could not be loaded.")
    } finally {
      setLoading(false)
    }
  }, [canView, date])

  useEffect(() => { void load() }, [load])

  const publish = async () => {
    if (!capability?.canPublish || publishInFlight.current || !date) return
    if (!register(true)) { setError("Save or cancel the open editor before publishing."); return }
    publishInFlight.current = true
    const key = operationId ?? crypto.randomUUID()
    setOperationId(key) // Retain on failure so an uncertain response retries the same operation.
    setPublishing(true)
    setError(null)
    try {
      const result = await api.post<{ publicationId: string; members: BenefitPledgeGeneration[] }>("/api/pledges/daily", { operationId: key, businessDate: date })
      setPublished(`Publication ${result.publicationId}: ${result.members.length} pledge(s) committed.`)
      setOperationId(null)
      await load()
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Publication failed. Retry uses the same operation identity.")
    } finally {
      publishInFlight.current = false
      setPublishing(false)
      register(false)
    }
  }

  if (!canView) {
    return <div className="p-6"><Alert variant="destructive"><AlertTitle>Not authorized</AlertTitle><AlertDescription>Your role does not have pledge-report view capability.</AlertDescription></Alert></div>
  }

  return <div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2"><IconReportMoney className="size-6" /><h1 className="text-2xl font-bold">Legacy daily publications</h1></div>
        <p className="mt-1 text-muted-foreground">Historical publication membership by Pacific business date. Publication is not submission and does not establish the new report baseline.</p>
      </div>
      <div className="flex items-center gap-2">
        <IconCalendar className="size-4 text-muted-foreground" />
        <Input aria-label="Pledge business date" type="date" value={date} disabled={publishing} onChange={(event) => { setDate(event.target.value); setOperationId(null); setPublished(null) }} />
        <Button variant="outline" onClick={() => void load()} disabled={loading || publishing}>{loading && <IconLoader2 className="size-4 animate-spin" />} Refresh</Button>
        <Button onClick={() => void publish()} disabled={!capability?.canPublish || publishing || loading || !date}>{publishing && <IconLoader2 className="size-4 animate-spin" />} {operationId ? "Retry publication" : "Publish pending pledges"}</Button>
      </div>
    </div>
    {capability?.disabledReason && <p className="text-sm text-muted-foreground">{capability.disabledReason}</p>}
    {published && <p role="status" className="text-sm">{published}</p>}
    {error && <Alert variant="destructive"><AlertTitle>Report unavailable</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
    <Card>
      <CardHeader><div className="flex items-center gap-2"><CardTitle>Daily pledges</CardTitle><Badge variant="outline">{report?.pledges.length ?? 0}</Badge></div><CardDescription>Membership and flags come from the committed publication output; later edits do not rewrite this report.</CardDescription></CardHeader>
      <CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Benefit</TableHead><TableHead>Program</TableHead><TableHead>Vendor</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Account</TableHead><TableHead>Status</TableHead><TableHead>Published flags</TableHead><TableHead>Captured</TableHead></TableRow></TableHeader><TableBody><PledgeRows rows={report?.pledges ?? []} /></TableBody></Table></CardContent>
    </Card>
    <Card>
      <CardHeader><div className="flex items-center gap-2"><IconHistory className="size-5" /><CardTitle>Removed follow-up</CardTitle><Badge variant="outline">{report?.removedFollowUp.length ?? 0}</Badge></div><CardDescription>Original positive amount and account evidence are retained with the removal occurrence. Current flags are shown separately from removal status/time; no reversal payment is created.</CardDescription></CardHeader>
      <CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Benefit</TableHead><TableHead>Program</TableHead><TableHead>Vendor</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Account</TableHead><TableHead>Status</TableHead><TableHead>Current flags</TableHead><TableHead>Removal recorded</TableHead></TableRow></TableHeader><TableBody><PledgeRows rows={report?.removedFollowUp ?? []} removed /></TableBody></Table></CardContent>
    </Card>
  </div>
}
